import {
  ApiStoreSummary,
  ApiStoreLanguage,
  ApiStoreCatalog,
  ApiStoreDetail,
  ApiStoreDetailDetails,
  ApiStoreDetailLocale,
  ApiStoreXConf,
  ApiStoreXConfProperty,
  ApiPhysicalStore,
  ApiStoreDetailLocation, ApiPhysicalStoreOpeningHours, ApiStoreDetailAlternateLocale
} from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.models';
import { ApiCmsConfiguration } from 'idt-ecommerce-api/_api/providers/api-cms/api-cms-configurations/api-cms-configurations.models';
import { AppIdEnum } from 'idt-ecommerce-api/config/config.models';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';

import {
  StoreDetailDetails, CurrencyInfo, StoreCmsConfigurationsObject, StoreXconfigurationsObject, PhysicalStore,
  StoreDetailLocation, PhysicalStoreSchedule, AlternateLocaleInfo, StoreSummary, StoreLanguage, StoreCatalog,
  StoreDetail
} from './stores.models';


/** Clase de utilidad para transformar los objetos que devuelve el api de inditex en los objetos que usaremos en el cms, widgets y render */
export class StoresModelsTransformer {

  public static storesFromApi(apiStores: ApiStoreDetail[]): StoreSummary[] {
    apiStores = this.applyWorldWideWorkaround(apiStores);

    const stores: StoreSummary[] = apiStores.map((apiStore) => {
      const store = this.storeSummaryFromApi(apiStore);
      return store;
    });

    return stores;
  }

  public static physicalStoreFromApi(apiPhysicalStore: ApiPhysicalStore, context: EcommerceContext): PhysicalStore {
    const physicalStore: PhysicalStore = {
      id: apiPhysicalStore.id,
      name: this.capitalize(apiPhysicalStore.name),
      photo: apiPhysicalStore.photo,
      addressLines: apiPhysicalStore.addressLines.map((address) => address.toLowerCase()),
      city: this.capitalize(apiPhysicalStore.city),
      zipCode: apiPhysicalStore.zipCode,
      phones: apiPhysicalStore.phones,
      state: this.capitalize(apiPhysicalStore.state),
      latitude: apiPhysicalStore.latitude,
      longitude: apiPhysicalStore.longitude,
      url: apiPhysicalStore.url,
    };

    if (isDefined(context?.config?.brandSettings?.schedulesFromApi)) {
      physicalStore.schedule = context.config.brandSettings.schedulesFromApi(apiPhysicalStore.openingHours, context);
    } else {
      physicalStore.schedule = this.schedulesFromApi(apiPhysicalStore.openingHours);
    }

    return physicalStore;
  }

  public static storeSummaryFromApi(apiStoreSummary: ApiStoreSummary): StoreSummary {
    const storeSummary: StoreSummary = {
      id: apiStoreSummary.id.toString(),
      countryCode: apiStoreSummary.countryCode,
      countryName: apiStoreSummary.countryName,
      continent: apiStoreSummary.continent,
      hostname: apiStoreSummary.hostname,
      supportedLanguages: this.storeLanguagesFromApi(apiStoreSummary.supportedLanguages),
      catalogs: this.storeCatalogsFromApi(apiStoreSummary.catalogs),
      isOpenForSale: apiStoreSummary.isOpenForSale,
      warehouseId: apiStoreSummary.ffmcenter,
    };

    if (apiStoreSummary.redirectToWorldWide) {
      storeSummary.redirectToWorldWide = apiStoreSummary.redirectToWorldWide;
    }

    if (apiStoreSummary.countryISOredirect) {
      storeSummary.countryISOredirect = apiStoreSummary.countryISOredirect;
    }

    return storeSummary;
  }

  public static storeDetailFromApi(apiStoreDetail: ApiStoreDetail, appId: AppIdEnum, apiCmsConfigurations?: ApiCmsConfiguration[]): StoreDetail {
    apiStoreDetail = this.applyWorldWideWorkaround([apiStoreDetail])[0];
    const storeDetail: StoreDetail = {
      ...this.storeSummaryFromApi(apiStoreDetail),
      details: this.storeDetailsFromApi(appId, apiStoreDetail.details, apiCmsConfigurations),
      storeDefaultLanguageId: isDefined(apiStoreDetail.storeDefaultLanguageId) ? apiStoreDetail.storeDefaultLanguageId.toString() : undefined
    };

    return storeDetail;
  }

  public static storeLanguagesFromApi(apiStoreLanguages: ApiStoreLanguage[]): StoreLanguage[] {
    const storeLanguages = apiStoreLanguages.map((apiStoreLanguage: ApiStoreLanguage) => {
      const langCode = this.applyLanguageWorkaroundIfNeccesary(apiStoreLanguage);

      const storeLanguage: StoreLanguage = {
        id: apiStoreLanguage.id.toString(),
        name: apiStoreLanguage.name,
        code: langCode,
        direction: apiStoreLanguage.isRTL ? 'rtl' : 'ltr'
      };

      return storeLanguage;
    });

    return storeLanguages;
  }

  public static storeCatalogsFromApi(apiStoreCatalogs: ApiStoreCatalog[]): StoreCatalog[] {
    const storeCatalogs = apiStoreCatalogs.map((apiStoreCatalog: ApiStoreCatalog) => {
      const storeCatalog: StoreCatalog = {
        id: apiStoreCatalog.id.toString(),
        type: apiStoreCatalog.type.toString(),
        identifier: apiStoreCatalog.identifier,
        label: `${apiStoreCatalog.identifier} (${apiStoreCatalog.id})`
      };

      return storeCatalog;
    });

    return storeCatalogs;
  }

  public static storeDetailsFromApi(
    appId: AppIdEnum,
    apiStoreDetailDetails?: ApiStoreDetailDetails,
    apiCmsConfigurations?: ApiCmsConfiguration[]
  ): StoreDetailDetails {
    const details: StoreDetailDetails = {};

    if (isDefined(apiStoreDetailDetails)) {
      details.imageBaseUrl = apiStoreDetailDetails.imageBaseUrl;
      details.staticBaseUrl = apiStoreDetailDetails.staticUrl;
      details.timestampBuildVersion = apiStoreDetailDetails.timestampBuildVersion;
      details._xmedia = apiStoreDetailDetails.xmedia;
      if (isDefined(apiStoreDetailDetails.location)) {
        details.location = this.storeLocationFromApi(apiStoreDetailDetails.location);
      }
      details.currencyInfo = this.storeCurrencyInfoFromApi(apiStoreDetailDetails.locale);

      if (apiStoreDetailDetails.alternateLocales.length > 0) {
        details.alternateLocaleInfo = this.storeAlternateInfoFromApi(apiStoreDetailDetails.alternateLocales[0]);
      }

      if (isDefined(apiCmsConfigurations)) {
        details.cmsConfigurations = this.cmsConfigurationsFromProviderApi(apiCmsConfigurations, appId);
      }
    }
    return details;
  }

  public static xConfigurationsFromApi(apiXConf: ApiStoreXConf): StoreXconfigurationsObject {
    let xConf: StoreXconfigurationsObject = {};

    if (isDefined(apiXConf)) {
      if (Array.isArray(apiXConf)) {
        apiXConf.forEach((i: ApiStoreXConfProperty) => {
          xConf = this.xConfValue(xConf, i.key, i.value);
        });
      } else {
        xConf = this.xConfValue(xConf, apiXConf.key, apiXConf.value);
      }
    }

    return xConf;
  }

  public static cmsConfigurationsFromProviderApi(apiCmsConfigurations: ApiCmsConfiguration[], appId: AppIdEnum): StoreCmsConfigurationsObject {
    const configurations: StoreCmsConfigurationsObject = {};
    for (const conf of apiCmsConfigurations) {
      // sample config
      const confValue = this.cmsConfigValue(conf, appId);
      if (conf.key === 'enabledGDPR' && conf.type === 'boolean' && isDefined(confValue)) {
        configurations.enabledGDPR = (confValue === 'true');
      }

      if (conf.key === 'helpCenterQuestions' && conf.type === 'json' && isDefined(confValue)) {
        configurations.helpCenterQuestions = JSON.parse(confValue);
      }

      if (conf.key === 'filtersMap' && conf.type === 'json' && isDefined(confValue)) {
        configurations.filtersMap = JSON.parse(confValue);
      }

      if (conf.key === 'groupsFilters' && conf.type === 'json' && isDefined(confValue)) {
        configurations.filterConfig = JSON.parse(confValue);
      }

      if (conf.key === 'lastSizesEnabled' && conf.type === 'boolean' && isDefined(confValue)) {
        configurations.lastSizesEnabled = (confValue === 'true');
      }

      if (conf.key === 'socialNetworks' && conf.type === 'json' && isDefined(confValue) && confValue !== '') {
        configurations.socialNetworks = JSON.parse(confValue);
      }

      if (conf.key === 'newsletterPrivacyPolicies' && conf.type === 'json' && isDefined(confValue)) {
        configurations.newsletterPrivacyPolicies = JSON.parse(confValue);
      }

      if (conf.key === 'enableAnalyticsRender' && conf.type === 'boolean' && isDefined(confValue)) {
        configurations.enableAnalyticsRender = (confValue === 'true');
      }

      if (conf.key === 'gtmId' && conf.type === 'string' && isDefined(confValue)) {
        configurations.gtmId = confValue;
      }
      // Add more confs
    }
    return configurations;
  }

  public static storeLocationFromApi(apiStoreLocation: ApiStoreDetailLocation): StoreDetailLocation {
    return {
      latitude: apiStoreLocation.lat,
      longitude: apiStoreLocation.lng,
      zoom: apiStoreLocation.zoom,
    };
  }

  public static storeCurrencyInfoFromApi(apiStoreLocale: ApiStoreDetailLocale | ApiStoreDetailAlternateLocale): CurrencyInfo {
    const currencyInfo = {
      currencyCode: apiStoreLocale.currencyCode,
      currencyFormatNeg: apiStoreLocale.currencyFormatNeg,
      currencyFormatPos: apiStoreLocale.currencyFormatPos,
      currencyDecimals: apiStoreLocale.currencyDecimals,
      currencySymbol: apiStoreLocale.currencySymbol,
      currencyGroupingChar: apiStoreLocale.currencygroupingChar || ',',
      currencyDecimalChar: apiStoreLocale.currencydecimalChar || '.'
    };

    return currencyInfo;
  }

  public static storeAlternateInfoFromApi(apiStoreDetailAlternateLocale: ApiStoreDetailAlternateLocale): AlternateLocaleInfo {
    return {
      ...this.storeCurrencyInfoFromApi(apiStoreDetailAlternateLocale),
      conversionRate: apiStoreDetailAlternateLocale.conversionRate
    };
  }

  // ---- Private functions ----

  /*
  * The worldwide store id is the only one not unique in the stores list
  *  For each store in the worldwide there will be one entry with the same
  * id and a different ISO code
  */
  private static applyWorldWideWorkaround(apiStores: ApiStoreDetail[]): ApiStoreDetail[] {
    const storesById: { [index: string]: ApiStoreDetail } = {};
    const catalogWW = apiStores.find(apiStore => apiStore.catalogs[0].identifier?.includes('WW'));
    const catalogWorldWide = {
      name: catalogWW?.catalogs[0].identifier,
      id: catalogWW?.catalogs[0].id
    };
    const worldWideType: { [index: string]: string } = {
      0: 'WW',
      1: 'WY',
      2: 'WZ',
      3: 'WA',
      4: 'WE',
      5: 'WM',
    };

    // WWs always has this 4 languages
    const WorldWideLanguages: ApiStoreLanguage[] = [
      { id: -1, name: 'English', code: 'en', isRTL: false },
      { id: -2, name: 'Français', code: 'fr', isRTL: false },
      { id: -5, name: 'Español', code: 'es', isRTL: false },
      { id: -6, name: 'Português', code: 'pt', isRTL: false }
    ];

    for (const apiStore of apiStores) {
      // for WWs, edit stores.helpers.ts >> getAllContinents() for workspace
      const copyApiStore = { ...apiStore, catalogs: apiStore.catalogs.map((x) => { return { ...x }; }) };

      if (apiStore.redirectToWorldWide) {
        copyApiStore.id = apiStore.redirectToWorldWide;
        delete copyApiStore.redirectToWorldWide;
        delete copyApiStore.countryISOredirect;

        // Add stores with id, but is in WW
        storesById[apiStore.id] = apiStore;
      }

      const apiStoreid = copyApiStore.id.toString();

      if (apiStoreid[1] === '8') {
        if (!storesById[copyApiStore.id]?.catalogs[0].identifier?.includes('WW')) {
          const worldWideid = apiStoreid[apiStoreid.length - 1];
          copyApiStore.countryCode = worldWideType[worldWideid];
          copyApiStore.countryName = `World Wide ${worldWideType[worldWideid][1]}`; // `World Wide W`
          copyApiStore.continent = '-1';
          copyApiStore.supportedLanguages = WorldWideLanguages;
          copyApiStore.storeDefaultLanguageId = -1;
          copyApiStore.isOpenForSale = true;
          this.applyWorldWideCatalog(copyApiStore, catalogWorldWide);

          // Add all WW stores
          storesById[copyApiStore.id] = copyApiStore;
        }
      } else {
        // Add stores with id, and is NOT in WW
        storesById[apiStore.id] = apiStore;
      }
    }

    return Object.values(storesById);
  }

  private static applyWorldWideCatalog(copyApiStore: ApiStoreDetail, catalogWorldWide: { name: string | undefined; id: number | undefined }) {
    if (catalogWorldWide.name != null && catalogWorldWide.id != null && !copyApiStore.catalogs[0].identifier?.includes('WW')) {
      for (const ct of copyApiStore.catalogs) {
        ct.identifier = catalogWorldWide.name;
        ct.id = catalogWorldWide.id;
      }
    }
  }

  private static cmsConfigValue(configuration: ApiCmsConfiguration, appId: AppIdEnum) {
    let value: string | undefined;
    switch (appId) {
      case AppIdEnum.web:
        value = configuration.webApp;
        break;
      case AppIdEnum.ios:
        value = configuration.iosApp;
        break;
      case AppIdEnum.android:
        value = configuration.androidApp;
        break;
      case AppIdEnum.store:
        value = configuration.storeApp;
        break;
      default:
        value = undefined;
    }
    return value;
  }

  private static schedulesFromApi(openingHours?: ApiPhysicalStoreOpeningHours): PhysicalStoreSchedule[] {
    if (!isDefined(openingHours)) { return []; }

    return openingHours.schedule.map((period) => ({
      hours: period.timeStripList.map((time) => `${time.initHour} - ${time.endHour}`),
      days: period.weekdays
    }));
  }

  private static xConfValue(xConf: StoreXconfigurationsObject, key: string, value: string) {
    const parsers: Record<string, (conf: StoreXconfigurationsObject, i: string) => StoreXconfigurationsObject> = {
      SEQUENCE_FROM_STOCK_ENABLED: (conf: StoreXconfigurationsObject, i: string) => {
        const copyConf: StoreXconfigurationsObject = {
          ...conf,
          liveSorting: i === '1'
        };
        return copyConf;
      },
      LAST_SIZES_ENABLED: (conf: StoreXconfigurationsObject, i: string) => {
        const copyConf: StoreXconfigurationsObject = {
          ...conf,
          lastSizesEnabled: i === '1'
        };
        return copyConf;
      }
    };

    return parsers[key](xConf, value);
  }

  private static capitalize(str?: string) {
    if (!isDefined(str)) {
      return '';
    }
    return str.toLowerCase().replace(/(?:^|\s|["'([{])+\S/g, (match) => match.toUpperCase());
  }

  // WORKAROUND: https://axinic.central.inditex.grp/jira/browse/MECFRONT-985
  // WORKAROUND: https://axinic.central.inditex.grp/jira/browse/MECFRONT-1841
  private static applyLanguageWorkaroundIfNeccesary(apiStoreLanguage: ApiStoreLanguage) {
    let langCode: string;
    if (apiStoreLanguage.code === 'iw') {
      langCode = 'he';
    } else if (apiStoreLanguage.code === 'in') {
      langCode = 'id';
    } else {
      langCode = apiStoreLanguage.code;
    }
    return langCode;
  }

}
